import { useEffect, useState } from "react";
import { theme } from "./theme";
import { useGame } from "./store";
import { chooseBestAction, type BotAction } from "../bot/minimax";
import {
  allLegalMoves,
  applyTurn,
  chebyshev,
  findAssaults,
  findEncounters,
  fromKey,
  isInEnemyTerritory,
  legalDestinationsAllModes,
  toAlgebraic,
  type Capture,
  type GameState,
  type Piece,
  type Side,
  type Square,
} from "../engine";

export type HintPanelLayout = "sidebar" | "strip";

interface HintExplanation {
  action: string;
  reasoning: string[];
}

/**
 * Guided-play hint panel.
 *
 * When hints are toggled on, this panel automatically computes the bot's best
 * action for the human's side at the start of each human turn and shows two
 * things: the mechanical move (what to do) and the strategic "why" behind it
 * (what to learn). Reasoning is derived from the actual position — capture
 * mechanics, piece-specific significance, defensive escapes, follow-up
 * threats, and territory/progression progress — not boilerplate.
 */
export function HintPanel({ layout }: { layout: HintPanelLayout }) {
  const game = useGame((s) => s.game);
  const humanSide = useGame((s) => s.humanSide);
  const pending = useGame((s) => s.pending);

  const [hint, setHint] = useState<HintExplanation | null>(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    if (game.victory) {
      setHint(null);
      setComputing(false);
      return;
    }
    if (game.toMove !== humanSide || pending) {
      setHint(null);
      setComputing(false);
      return;
    }
    setHint(null);
    setComputing(true);
    const t = setTimeout(() => {
      const action = chooseBestAction(game, humanSide, {
        deadlineMs: performance.now() + 900,
        maxDepth: 4,
      });
      if (action) setHint(describeAction(game, humanSide, action));
      setComputing(false);
    }, 50);
    return () => clearTimeout(t);
  }, [game, humanSide, pending]);

  const isSidebar = layout === "sidebar";

  if (game.victory) return null;

  const wrapper = isSidebar ? sidebarStyle : stripStyle;

  if (game.toMove !== humanSide) {
    return (
      <div style={wrapper}>
        <span style={{ color: theme.boardLabel, fontSize: 12 }} className="rune">
          {isSidebar ? "Guided play · opponent's turn…" : "Opponent's turn…"}
        </span>
      </div>
    );
  }
  if (pending) return null;

  return (
    <div style={wrapper}>
      <span style={{ color: theme.boardLabel, fontSize: 12 }} className="rune">
        {isSidebar ? "Guided play · suggested move" : "Suggested move"}
      </span>
      {computing || !hint ? (
        <span
          style={{
            color: theme.boardLabel,
            fontSize: isSidebar ? 14 : 13,
            fontStyle: "italic",
            lineHeight: 1.5,
          }}
        >
          Thinking…
        </span>
      ) : (
        <>
          <span
            style={{
              color: theme.parchment,
              fontSize: isSidebar ? 14 : 13,
              lineHeight: 1.5,
            }}
          >
            {hint.action}
          </span>
          {hint.reasoning.length > 0 && (
            <div
              style={{
                color: theme.parchment,
                fontSize: isSidebar ? 13 : 12,
                lineHeight: 1.55,
                opacity: 0.82,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingTop: 6,
                borderTop: `1px dashed ${theme.boardEdge}`,
              }}
            >
              {hint.reasoning.map((sentence, i) => (
                <span key={i}>{sentence}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function describeAction(state: GameState, side: Side, action: BotAction): HintExplanation {
  const piece = state.board.get(squareKey(action.move.from));
  if (!piece) return { action: "Hint unavailable.", reasoning: [] };

  const pieceLabel = `${capitalize(piece.shape)}-${piece.value}`;
  const fromAlg = toAlgebraic(action.move.from);
  const toAlg = toAlgebraic(action.move.to);
  const modeLabel = action.move.mode ? ` (as ${capitalize(action.move.mode)})` : "";

  let actionLine = `Move ${pieceLabel} from ${fromAlg} to ${toAlg}${modeLabel}.`;

  if (action.declared) {
    const target = state.board.get(squareKey(action.declared.target));
    const targetLabel = target ? `${capitalize(target.shape)}-${target.value}` : "the enemy piece";
    const targetAlg = toAlgebraic(action.declared.target);
    actionLine += ` Declare ${action.declared.method.toUpperCase()} on ${targetAlg}: ${targetLabel} via ${action.declared.formula}.`;
    if (action.advance) actionLine += " Advance into the vacated square.";
  }

  const reasoning = action.declared
    ? captureReasoning(state, side, action, piece)
    : positionalReasoning(state, side, action, piece);

  return { action: actionLine, reasoning };
}

function captureReasoning(
  state: GameState,
  side: Side,
  action: BotAction,
  attacker: Piece,
): string[] {
  const declared = action.declared!;
  const target = state.board.get(squareKey(declared.target));
  if (!target) return [];

  const reasons: string[] = [];

  switch (declared.method) {
    case "encounter":
      reasons.push(
        `Encounter: same-value pieces dissolve into each other. Your ${attacker.value} meets the enemy ${target.value} in legal range, and the enemy is removed without you losing anything.`,
      );
      break;
    case "assault":
      reasons.push(
        `Assault: two of your pieces combine — ${declared.formula} — to match the enemy's value. Both attackers stay put; only the enemy is removed. Setups like this are scarce because both your pieces must have legal reach to the same target.`,
      );
      break;
    case "siege":
      reasons.push(
        `Siege: the enemy is sealed on all four orthogonal sides by your pieces or the board edge. No value math needed — pure positional pressure. Sieges fire automatically the moment the surround completes.`,
      );
      break;
    case "deceit":
      reasons.push(
        `Deceit (Encounter + advance): your piece steps into the captured square. Same arithmetic as Encounter, but you gain ground in addition to the capture.`,
      );
      break;
  }

  if (target.shape === "pyramid") {
    reasons.push(
      "The Pyramid is the most flexible piece in the game — it can move as Round, Triangle, or Square each turn. Removing it strips the opponent's biggest mobility threat. Take it whenever the math allows.",
    );
  } else if (target.shape === "square" && target.value >= 36) {
    reasons.push(
      `Squares move three squares orthogonally and need a clear path — they're slow and easy to block. A ${target.value} this exposed is unusual; high-value pieces like this one are the heaviest material swings on the board.`,
    );
  } else if (target.shape === "triangle" && target.value >= 20) {
    reasons.push(
      "Triangles move two orthogonally with a knight's-hop flying option — fast to reposition, but their standard reach (the only one that captures) only covers four squares. Catching a high-value Triangle on a matching square is hard to engineer.",
    );
  } else if (target.shape === "round") {
    reasons.push(
      "Rounds (one-step in any direction) are the workhorses of Encounter captures — small values, but each capture chips away at the enemy line.",
    );
  }

  if (state.victoryConfig.level === "common" || state.victoryConfig.level === "proper") {
    const current = state.captured[side].reduce((a, p) => a + p.value, 0);
    const after = current + target.value;
    const threshold = state.victoryConfig.commonThreshold;
    if (after >= threshold) {
      reasons.push(`Score: ${after}/${threshold} — this capture clinches the Common Victory.`);
    } else {
      reasons.push(`Score: ${current} → ${after}/${threshold}. ${threshold - after} more points to the win.`);
    }
  }

  if (action.advance && isInEnemyTerritory(side, declared.target.rank)) {
    reasons.push(
      "Advancing into the vacated square plants this piece in enemy territory — exactly where it needs to be to count toward Greater/Royal/Glorious progression victories.",
    );
  }

  return reasons;
}

function positionalReasoning(
  state: GameState,
  side: Side,
  action: BotAction,
  fromPiece: Piece,
): string[] {
  const enemy: Side = side === "white" ? "black" : "white";
  const reasons: string[] = [];

  // 1. Defense: was the from-square under threat?
  const incoming: Capture[] = [
    ...findEncounters(state, enemy),
    ...findAssaults(state, enemy),
  ].filter((c) => sameSquare(c.target, action.move.from));
  if (incoming.length > 0) {
    const t = incoming[0];
    reasons.push(
      `Your ${capitalize(fromPiece.shape)}-${fromPiece.value} was under attack at ${toAlgebraic(action.move.from)} — the enemy ${t.method.toUpperCase()} (${t.formula}) would have taken it next turn. Sidestepping costs no material; staying would have cost ${fromPiece.value}.`,
    );
  }

  // 2. Compute the post-move position so we can see what the opponent has to deal with.
  let post: GameState | null = null;
  try {
    const result = applyTurn(state, action.move, { declared: action.declared, advance: action.advance });
    post = result.state;
  } catch {
    post = null;
  }

  // 3. Immediate threat vs. multi-step setup. If we've already got a one-ply threat queued up,
  //    that's what we should be teaching — don't dilute it with a vaguer "setting up" line.
  if (post) {
    const threatLine = describeThreatsAfter(post, side);
    if (threatLine) {
      reasons.push(threatLine);
    } else {
      const setupLine = describeFutureSetup(state, post, side, action, fromPiece);
      if (setupLine) reasons.push(setupLine);
    }
  }

  // 4. Territory crossing — only call this out at progression-victory levels, where it matters.
  const enteredEnemy =
    isInEnemyTerritory(side, action.move.to.rank) &&
    !isInEnemyTerritory(side, action.move.from.rank);
  const progressionLevel =
    state.victoryConfig.level === "greater" ||
    state.victoryConfig.level === "royal" ||
    state.victoryConfig.level === "glorious";
  if (enteredEnemy && progressionLevel) {
    reasons.push(
      "Crosses into enemy territory. Only pieces in this half of the board count toward forming the progression you need for this victory level.",
    );
  }

  // 5. Last-resort fallback: every move teaches *something*. If we couldn't surface a defense,
  //    threat, or setup, describe the move's positional intent against the actual board — what
  //    enemy pieces it gets closer to, what lane it occupies, etc. — rather than parroting
  //    generic piece trivia.
  if (reasons.length === 0 && post) {
    const advancement = describeAdvancement(state, post, side, action, fromPiece);
    if (advancement) reasons.push(advancement);
  }

  return reasons;
}

/**
 * "What is this move building toward?" Three concrete setups, checked in priority order:
 *   1. Encounter approach — moves closer to a same-value enemy piece (future 1v1 capture).
 *   2. Assault formation — pairs with another friendly piece whose values combine (sum, diff,
 *      or product) to match a nearby enemy. Both pieces must already be plausibly within reach
 *      of the target (Chebyshev ≤ 4).
 *   3. Path clearing — Squares' standard 3-orthogonal move needs a clear path (their flying
 *      option is non-capturing), so vacating a from-square sometimes unblocks one of your
 *      Squares to reach a previously unreachable capture target.
 * Returns null only if none of these reflect the actual position.
 */
function describeFutureSetup(
  before: GameState,
  after: GameState,
  side: Side,
  action: BotAction,
  fromPiece: Piece,
): string | null {
  const approach = findEncounterApproach(after, side, action, fromPiece);
  if (approach) {
    return `Closes distance toward the enemy ${capitalize(approach.enemy.shape)}-${approach.enemy.value} at ${toAlgebraic(approach.square)} (${approach.distBefore} → ${approach.distAfter} squares). Matching values, so once you can reach that square, that's an Encounter — this move builds the lane for it.`;
  }

  const assault = findAssaultFormation(after, side, fromPiece, action);
  if (assault) {
    return `Sets up an Assault: your ${capitalize(fromPiece.shape)}-${fromPiece.value} and the ${capitalize(assault.partner.shape)}-${assault.partner.value} at ${toAlgebraic(assault.partnerSquare)} combine to ${assault.formula} = the enemy ${capitalize(assault.target.shape)}-${assault.target.value} at ${toAlgebraic(assault.targetSquare)}. If you can maneuver both within legal reach of that target, the capture fires.`;
  }

  const clearing = findPathClearing(before, after, side, action);
  if (clearing) {
    return `Unblocks a Square lane: your Square-${clearing.piece.value} at ${toAlgebraic(clearing.from)} now has destinations the moved piece was blocking. Squares' capturing move can't leap, so opening their corridors is how you get them into a position to take pieces.`;
  }

  return null;
}

/** Closest same-value enemy that this move gets us nearer to (Chebyshev distance). */
function findEncounterApproach(
  after: GameState,
  side: Side,
  action: BotAction,
  fromPiece: Piece,
): { enemy: Piece; square: Square; distBefore: number; distAfter: number } | null {
  let best: { enemy: Piece; square: Square; distBefore: number; distAfter: number } | null = null;
  for (const [k, p] of after.board) {
    if (p.side === side) continue;
    if (p.value !== fromPiece.value) continue;
    const sq = fromKey(k);
    const distBefore = chebyshev(action.move.from, sq);
    const distAfter = chebyshev(action.move.to, sq);
    if (distAfter >= distBefore) continue;
    if (!best || distAfter < best.distAfter) {
      best = { enemy: p, square: sq, distBefore, distAfter };
    }
  }
  return best;
}

/** Find an enemy whose value matches sum/diff/product of `fromPiece` and some other friendly. */
function findAssaultFormation(
  after: GameState,
  side: Side,
  fromPiece: Piece,
  action: BotAction,
): { partner: Piece; partnerSquare: Square; target: Piece; targetSquare: Square; formula: string } | null {
  const myNewSquare = action.move.to;
  for (const [pk, partner] of after.board) {
    if (partner.side !== side) continue;
    const pSq = fromKey(pk);
    if (sameSquare(pSq, myNewSquare)) continue;
    for (const [ek, enemy] of after.board) {
      if (enemy.side === side) continue;
      const eSq = fromKey(ek);
      let formula: string | null = null;
      if (fromPiece.value + partner.value === enemy.value) {
        formula = `${fromPiece.value} + ${partner.value}`;
      } else if (Math.abs(fromPiece.value - partner.value) === enemy.value && enemy.value > 0) {
        const hi = Math.max(fromPiece.value, partner.value);
        const lo = Math.min(fromPiece.value, partner.value);
        formula = `${hi} − ${lo}`;
      } else if (fromPiece.value * partner.value === enemy.value && partner.value > 1 && fromPiece.value > 1) {
        formula = `${fromPiece.value} × ${partner.value}`;
      }
      if (!formula) continue;
      // Both pieces need to plausibly be within striking distance of the target.
      if (chebyshev(myNewSquare, eSq) > 4) continue;
      if (chebyshev(pSq, eSq) > 4) continue;
      return { partner, partnerSquare: pSq, target: enemy, targetSquare: eSq, formula };
    }
  }
  return null;
}

/** Did vacating `action.move.from` open new destinations for one of our Squares? */
function findPathClearing(
  before: GameState,
  after: GameState,
  side: Side,
  action: BotAction,
): { piece: Piece; from: Square } | null {
  for (const [k, p] of after.board) {
    if (p.side !== side) continue;
    if (p.shape !== "square") continue;
    const sq = fromKey(k);
    if (sameSquare(sq, action.move.to)) continue;
    const beforeDests = new Set(legalDestinationsAllModes(before, sq).map((d) => squareKey(d.to)));
    const afterDests = legalDestinationsAllModes(after, sq).map((d) => squareKey(d.to));
    if (afterDests.some((d) => !beforeDests.has(d))) {
      return { piece: p, from: sq };
    }
  }
  return null;
}

/**
 * Position-aware fallback for the quieter moves the heuristics above don't catch. Picks the
 * *nearest* enemy piece this move brings us closer to and frames the advancement around it —
 * concrete, not generic — so the player learns to read what their pieces are pressuring.
 */
function describeAdvancement(
  _before: GameState,
  after: GameState,
  side: Side,
  action: BotAction,
  fromPiece: Piece,
): string | null {
  // Find the nearest enemy whose distance dropped.
  let closest: { enemy: Piece; square: Square; distAfter: number } | null = null;
  for (const [k, p] of after.board) {
    if (p.side === side) continue;
    const sq = fromKey(k);
    const distAfter = chebyshev(action.move.to, sq);
    const distBefore = chebyshev(action.move.from, sq);
    if (distAfter > distBefore) continue;
    if (!closest || distAfter < closest.distAfter) {
      closest = { enemy: p, square: sq, distAfter };
    }
  }

  const enemyDir = side === "white" ? -1 : 1;
  const ranksAdvanced = (action.move.to.rank - action.move.from.rank) * enemyDir;

  if (closest && closest.distAfter <= 5) {
    const enemyLabel = `${capitalize(closest.enemy.shape)}-${closest.enemy.value}`;
    return `Positions your ${capitalize(fromPiece.shape)}-${fromPiece.value} ${closest.distAfter} squares from the enemy ${enemyLabel} at ${toAlgebraic(closest.square)}. Not a direct threat yet, but the move puts you in their neighborhood — the kind of slow pressure that forces them to react instead of developing freely.`;
  }

  if (ranksAdvanced >= 1) {
    return `Pushes the ${capitalize(fromPiece.shape)}-${fromPiece.value} ${ranksAdvanced} rank${ranksAdvanced > 1 ? "s" : ""} toward enemy territory. Pieces sitting on your back ranks do no work — getting them forward is what turns positions into captures later.`;
  }

  // Lateral or backward — describe what the file change actually is. The previous version of
  // this branch claimed every lateral move "keeps the piece on a useful column rather than out
  // on the wing", which was wrong half the time (moves heading *toward* the wing were
  // described as moves away from it).
  const pieceLabel = `${capitalize(fromPiece.shape)}-${fromPiece.value}`;
  const fromCenterDist = Math.abs(action.move.from.file - 3.5);
  const toCenterDist = Math.abs(action.move.to.file - 3.5);
  const isCentralizing = toCenterDist < fromCenterDist;
  const isWingward = toCenterDist > fromCenterDist;
  const isRetreating = ranksAdvanced < 0;
  const wingName = action.move.to.file < 3.5 ? "A-file side" : "H-file side";

  const enemyContext = closest
    ? ` Now ${closest.distAfter} squares from the enemy ${capitalize(closest.enemy.shape)}-${closest.enemy.value} at ${toAlgebraic(closest.square)}.`
    : "";

  if (isRetreating) {
    return `Pulls the ${pieceLabel} back ${-ranksAdvanced} rank${-ranksAdvanced > 1 ? "s" : ""} — a regrouping move, usually to dodge a threat the search saw forming or to free up a lane for a piece behind it.${enemyContext}`;
  }
  if (isCentralizing) {
    return `Centralizes the ${pieceLabel}, pulling it toward the D–E files where it covers both flanks instead of being stuck on one side.${enemyContext}`;
  }
  if (isWingward) {
    return `Shifts the ${pieceLabel} toward the ${wingName} of the board. Wings have less reach back to the center, but the move usually opens a flank line or sidesteps an enemy piece pinning the central files.${enemyContext}`;
  }
  // Same file — pure rank-zero move, e.g. a Triangle's knight-hop that lands back on its own file.
  // Rare; just describe the lateral repositioning honestly.
  return `Repositions the ${pieceLabel} laterally without changing its file or its rank meaningfully.${enemyContext}`;
}

/**
 * Given the position AFTER our move (where it's now the opponent's turn), describe what they
 * have to defend against. Pulls in opponent-aware reasoning where it exists: forks (multiple
 * targets), forced moves (one safe escape), or stuck pieces (no safe escape). Returns null
 * if our move creates no immediate threat — we won't pad with filler.
 */
function describeThreatsAfter(post: GameState, attackerSide: Side): string | null {
  const raw: Capture[] = [
    ...findEncounters(post, attackerSide),
    ...findAssaults(post, attackerSide),
  ];
  if (raw.length === 0) return null;

  // Dedupe by target square — multiple ways to capture the same piece still counts as one threat.
  const byTarget = new Map<string, Capture>();
  for (const t of raw) {
    const k = squareKey(t.target);
    if (!byTarget.has(k)) byTarget.set(k, t);
  }
  const threats = [...byTarget.values()];
  threats.sort((a, b) => {
    const va = post.board.get(squareKey(a.target))?.value ?? 0;
    const vb = post.board.get(squareKey(b.target))?.value ?? 0;
    return vb - va;
  });

  const top = threats[0];
  const target = post.board.get(squareKey(top.target));
  if (!target) return null;

  const targetLabel = `${capitalize(target.shape)}-${target.value}`;
  const targetAlg = toAlgebraic(top.target);
  const base = `After moving, you'll threaten a ${top.method.toUpperCase()} on the enemy ${targetLabel} at ${targetAlg} via ${top.formula}.`;

  // Fork: more than one distinct piece comes under attack at once.
  if (threats.length >= 2) {
    const second = post.board.get(squareKey(threats[1].target));
    if (second) {
      return `${base} You also threaten their ${capitalize(second.shape)}-${second.value} at ${toAlgebraic(threats[1].target)} on the same turn — they can save one or the other, not both.`;
    }
  }

  // Single threat — how trapped is the target?
  const safeEscapes = countSafeEscapes(post, top.target, attackerSide);
  if (safeEscapes === 0) {
    return `${base} The ${targetLabel} has nowhere safe to retreat — every legal move keeps it (or another piece) inside your capture range, so they lose material no matter what.`;
  }
  if (safeEscapes === 1) {
    return `${base} The ${targetLabel} has exactly one safe square to flee to. Their reply is forced, which means you already know the position you'll be playing into.`;
  }
  return base;
}

/**
 * For the given threatened piece at `threatenedSquare`, count how many of its own legal moves
 * land it on a square where the attacker can no longer capture it. Cheap one-ply lookahead —
 * doesn't model the opponent's other defensive options (blocking the attacker, capturing the
 * attacker first), but enough to flag "stuck" or "forced" pieces.
 */
function countSafeEscapes(post: GameState, threatenedSquare: Square, attackerSide: Side): number {
  const targetMoves = allLegalMoves(post).filter((m) => sameSquare(m.from, threatenedSquare));
  let safe = 0;
  for (const m of targetMoves) {
    try {
      const after = applyTurn(post, m, {});
      const stillCapturable = [
        ...findEncounters(after.state, attackerSide),
        ...findAssaults(after.state, attackerSide),
      ].some((c) => sameSquare(c.target, m.to));
      if (!stillCapturable) safe++;
    } catch {
      /* move turned out to be invalid; skip */
    }
  }
  return safe;
}

function squareKey(sq: Square): `${number},${number}` {
  return `${sq.file},${sq.rank}`;
}

function sameSquare(a: Square, b: Square): boolean {
  return a.file === b.file && a.rank === b.rank;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const stripStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 6,
  padding: "8px 14px",
  background: theme.bg,
  borderTop: `1px solid ${theme.boardEdge}`,
  borderBottom: `1px solid ${theme.boardEdge}`,
};

const sidebarStyle: React.CSSProperties = {
  width: 260,
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  gap: 8,
  padding: "16px 14px",
  background: theme.bg,
  borderLeft: `1px solid ${theme.boardEdge}`,
  overflowY: "auto",
};
